// Scherm 16 — de gebeurtenis-feed. Hij staat in de workspace, want dat is waar
// V2 hem tekent (r. 3819, "SECTIE 3: INZICHTEN") en het menu blijft daarmee op
// de zeven items van W4.
//
// Vorm letterlijk uit V2_frontend.jsx: filterpillen (radius 99, actief #111827),
// kaarten van 15px/18px met rand #ececec en radius 12, hover #d1d5db met een
// lichte schaduw, kopregel met badge links en datum rechts, titel 14/600.
// De severity-kleuren zijn die van V2 r. 15 — ze staan al in tokens.ts.
//
// W8-oordeel scherm 16: "voorbereiden, aan bij ronde 2". Dat is hier letterlijk
// zichtbaar in plaats van weggelaten: acht van de elf gebeurtenistypen staan in
// config/feed.yaml op `active: false` omdat hun databron nog niet bestaat, en de
// drie die wél aan staan zeggen in ronde 1 iets anders dan ze lijken te zeggen.
// Gemeten op 2026-08-24: alle 3.563 trends en 1.766 skills dragen als
// ontdekkingsdatum de dag van de nulmeting (18-21 augustus), tegenover 44 échte
// nieuwe bronnen. Zonder die banner leest een gebruiker 5.329 items als nieuws.

import { useEffect, useMemo, useState } from "react";

import { BewijsBadge, Bewijsbalk, WaaropPil, WiePil } from "./Bewijs";
import { EmptyList, LoadError } from "./Empty";
import { SkillDetail } from "./SkillDetail";
import { SourceDetail } from "./SourceDetail";
import { TrendDetail } from "./TrendDetail";
import { TrendVanBevinding } from "./TrendlaagOud";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { SlidePanel } from "./ui";
import type {
  FeedItem,
  Skill,
  SkillDetailResponse,
  Source,
  SourceDetailResponse,
  Trend,
  TrendDetailResponse,
} from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { useActieveSector } from "../lib/sectorkeuze";
import { useResource } from "../lib/useResource";
import { propositiesVan, toonPropositie } from "../nieuw/propositie";
import { chart, color, radius, size, weight } from "../tokens";

const LIMIT = 50;

// Machine-waarde -> wat er op het scherm staat. Eén plek per as (backlog 30);
// de as zelf is de enum uit api/feed.schema.json, niet een lijst van ons.
const TYPE_LABEL: Record<string, string> = {
  new_trend_detected: "Nieuwe trend",
  new_skill_detected: "Nieuwe vaardigheid",
  new_source: "Nieuwe bron",
  priority_shift: "Prioriteit verschoven",
  cross_sector_signal: "Cross-sectorsignaal",
  funding_alert: "Subsidiesignaal",
  implication_detected: "Implicatie",
  score_change: "Score veranderd",
  proposition_gap: "Gat in de propositie",
  research_complete: "Onderzoek afgerond",
  topic_warming: "Onderwerp warmt op",
};

// Volgorde van de bakken op het scherm: zoals de API sorteert (severity), zodat
// het menu dezelfde volgorde heeft als de lijst eronder.
const TYPE_ORDER = ["new_trend_detected", "new_skill_detected", "new_source"];

// De vier severity-kleuren van V2 r. 15 — geen nieuwe waarden, ze staan al in
// tokens.ts als het CHART-palet.
const SEVERITY: Record<string, string> = {
  high: chart.red,
  medium: chart.amber,
  low: chart.green,
  info: chart.blue,
};

const SEVERITY_UITLEG: Record<string, string> = {
  high: "Hoog — vraagt aandacht deze ronde.",
  medium: "Gemiddeld — nieuw in beeld.",
  low: "Laag — goed om te weten.",
  info: "Informatief.",
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

function Chip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  /** null = geen telling (de propositiechips). */
  count: number | null;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      aria-pressed={active}
      onClick={onClick}
      style={{
        background: active ? color.ink : color.surface,
        color: active ? color.surface : color.soft,
        border: `1px solid ${active ? color.ink : color.border}`,
        borderRadius: radius.pill,
        padding: "5px 13px",
        fontSize: 12,
        fontWeight: weight.regular,
        fontFamily: "inherit",
        cursor: "pointer",
        transition: "all 0.12s",
      }}
      onMouseEnter={(e) => {
        if (active) return;
        e.currentTarget.style.borderColor = color.disabled;
        e.currentTarget.style.color = color.body;
      }}
      onMouseLeave={(e) => {
        if (active) return;
        e.currentTarget.style.borderColor = color.border;
        e.currentTarget.style.color = color.soft;
      }}
    >
      {label}{count === null ? "" : ` ${count.toLocaleString("nl-NL")}`}
    </button>
  );
}

function FeedRow({ item, onOpen }: { item: FeedItem; onOpen: (it: FeedItem) => void }) {
  const toekomst = Date.parse(item.date) > Date.now();
  return (
    <div
      onClick={() => onOpen(item)}
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: "15px 18px",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color.disabled;
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = color.line;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            title={SEVERITY_UITLEG[item.severity] ?? item.severity}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: SEVERITY[item.severity] ?? color.faint,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10.5,
              fontWeight: weight.bold,
              color: color.soft,
              background: color.lineFaint,
              padding: "3px 9px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            {TYPE_LABEL[item.type] ?? item.type}
          </span>
          {item.category && (
            <span
              style={{
                fontSize: size.meta,
                color: color.faint,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.category}
            </span>
          )}
          {/* Methode v3: bij een nieuwe bron wie het zegt en waarop, naast de
              uitgever. Zonder de velden rendert dit niets. */}
          {item.type === "new_source" && (
            <>
              <WiePil bron={item} />
              <WaaropPil bron={item} />
            </>
          )}
        </div>
        {/* Negen gepubliceerde bronnen dragen uit hun eigen metadata een datum
            in de toekomst (de EU AI Act-tijdlijn zegt 2028-08-02). Die staan
            hier bovenaan, want de feed sorteert op datum — dus zegt de rij het
            erbij, net als op de bronnenlijst. */}
        <span
          title={
            toekomst
              ? "De bron vermeldt zelf deze datum; hij ligt in de toekomst."
              : undefined
          }
          style={{
            fontSize: 12,
            color: toekomst ? color.accentAmber : color.faint,
            flexShrink: 0,
          }}
        >
          {fmtDate(item.date)}
          {toekomst && " · in de toekomst"}
        </span>
      </div>
      <div
        style={{
          fontSize: size.body,
          fontWeight: weight.medium,
          color: color.ink,
          lineHeight: 1.4,
          marginBottom: 4,
        }}
      >
        {item.subject.name}
      </div>
      {/* W8-regel 2 is "nooit een kale score", en de owner-review van sessie 1
          was "al die pillen zijn te veel informatie". Dezelfde oplossing als in
          de lijsten: de score staat er, de reden hangt eraan (tooltip) in plaats
          van dat vijf badges de rij overnemen. */}
      {/* Methode v3: draagt de trend bewijs, dan zegt de regel wie hem
          bevestigt, met het kleine balkje en wat er mist — in plaats van de
          uitlichtscore met zijn gezag-badge. */}
      {/* Trendlaag aan: geen keurmerk (bevestigd/gesignaleerd) of uitlichtscore bij een trend. */}
      {trendlaagAan() && item.itemKind === "trend" ? null : item.bewijs ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <BewijsBadge bewijs={item.bewijs} />
          <Bewijsbalk bewijs={item.bewijs} klein />
        </span>
      ) : typeof item.uitlichtscore === "number" && (item.uitlichtBadges?.length ?? 0) > 0 && (
        <span
          title={`Uitgelicht omdat: ${(item.uitlichtBadges ?? []).join(" · ")}`}
          style={{ fontSize: 12, color: color.faint, borderBottom: `1px dotted ${color.disabled}` }}
        >
          uitgelicht {item.uitlichtscore.toFixed(1)} — {item.uitlichtBadges?.[0]}
          {(item.uitlichtBadges?.length ?? 0) > 1
            ? ` +${(item.uitlichtBadges?.length ?? 1) - 1}`
            : ""}
        </span>
      )}
    </div>
  );
}

export function EventFeed() {
  const [gekozen, setGekozen] = useState<string[]>([]);
  const [open, setOpen] = useState<FeedItem | null>(null);
  const [trend, setTrend] = useState<Trend | null>(null);
  const [skill, setSkill] = useState<SkillDetailResponse | null>(null);
  const [bron, setBron] = useState<Source | null>(null);

  // Filter Propositie (ADR 2026-10-23-ict-los-platform): pas bij twee of meer
  // zichtbare proposities. Daarvoor vraagt de feed zonder sector, precies zoals
  // vóór de ADR (de funding-bak leest dan het hele register).
  const meta = useMeta();
  const start = useActieveSector();
  const [eigen, setEigen] = useState<string | null>(null);
  const metPropositie = toonPropositie(meta);
  const sector = metPropositie ? (eigen ?? start ?? undefined) : undefined;
  const key = gekozen.join("|") + "#" + (sector ?? "");
  const feed = useResource(
    () => api.feed({ limit: LIMIT, type: gekozen.length ? gekozen : undefined, sector }),
    [key],
  );

  const counts = feed.data?.counts ?? {};
  const items = feed.data?.items ?? [];
  const total = feed.data?.total ?? items.length;
  const baseline = feed.data?.baseline !== false;

  const bakken = useMemo(
    () =>
      Object.keys(counts).sort(
        (a, b) =>
          (TYPE_ORDER.indexOf(a) + 1 || 99) - (TYPE_ORDER.indexOf(b) + 1 || 99),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(counts)],
  );

  // Het detailpaneel: de feed draagt alleen id en naam, dus de volledige rij
  // wordt opgehaald bij het openen. Mislukt dat, dan sluit het paneel met de
  // reden in plaats van een leeg vlak te tonen.
  const [detailFout, setDetailFout] = useState<string | null>(null);
  useEffect(() => {
    setTrend(null);
    setSkill(null);
    setBron(null);
    setDetailFout(null);
    if (!open) return;
    let levend = true;
    const mis = () => levend && setDetailFout("Deze gebeurtenis kon niet geopend worden.");
    if (open.itemKind === "trend") {
      // Trendlaag aan: TrendVanBevinding haalt zelf op (via /trendlaag/van).
      if (trendlaagAan()) return;
      api
        .trend(open.subject.id)
        .then((d: TrendDetailResponse) => levend && setTrend(d.trend))
        .catch(mis);
    } else if (open.itemKind === "skill") {
      api
        .skill(open.subject.id)
        .then((d: SkillDetailResponse) => levend && setSkill(d))
        .catch(mis);
    } else if (open.itemKind === "bron") {
      api
        .source(open.subject.id)
        .then((d: SourceDetailResponse) => levend && setBron(d.source))
        .catch(mis);
    }
    return () => {
      levend = false;
    };
  }, [open]);

  const sluit = () => setOpen(null);

  return (
    <div>
      {baseline && (
        <div
          style={{
            background: color.accentAmberBg,
            border: `1px solid ${color.accentAmber}33`,
            borderRadius: radius.block,
            padding: "12px 16px",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: size.meta,
              fontWeight: weight.medium,
              color: color.accentAmber,
              marginBottom: 4,
            }}
          >
            Dit is de nulmeting, geen nieuws
          </div>
          <p style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.55 }}>
            Trends en vaardigheden dragen als ontdekkingsdatum de dag waarop ze
            voor het eerst werden gelezen, niet de dag waarop ze ontstonden. Die
            eerste lezing staat daarom niet als
            nieuws in deze lijst; de volledige collectie staat op de Trends- en
            Skills-tabs. Wat hier wél staat is echt gebeurd: nieuwe bronnen
            (met de datum van het document zelf) en subsidievensters.
          </p>
        </div>
      )}

      {metPropositie && (
        <div role="group" aria-label="Propositie" style={{ display: "flex", gap: 5, marginBottom: 8, flexWrap: "wrap" }}>
          {propositiesVan(meta).map((p) => (
            <Chip key={p.sector} label={p.naam} count={null} active={sector === p.sector} onClick={() => setEigen(p.sector)} />
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 5, marginBottom: 12, flexWrap: "wrap" }}>
        {bakken.map((t) => (
          <Chip
            key={t}
            label={TYPE_LABEL[t] ?? t}
            count={counts[t] ?? 0}
            active={gekozen.includes(t)}
            onClick={() =>
              setGekozen((g) => (g.includes(t) ? g.filter((x) => x !== t) : [...g, t]))
            }
          />
        ))}
      </div>

      {/* De telregel van W1c §7: het getal boven de lijst is het getal van deze
          weergave, en als de limiet knipt staat erbij hoeveel je ziet. */}
      <p style={{ fontSize: size.meta, color: color.faint, marginBottom: 10 }}>
        {feed.loading
          ? "laden…"
          : `${total.toLocaleString("nl-NL")} ${
              total === 1 ? "gebeurtenis" : "gebeurtenissen"
            }${gekozen.length ? " in deze selectie" : ""}` +
            (items.length < total ? ` · ${items.length} getoond` : "")}
      </p>

      {feed.error ? (
        <LoadError reason={feed.error} onRetry={feed.reload} />
      ) : items.length === 0 && !feed.loading ? (
        <EmptyList
          reason={
            gekozen.length
              ? "Geen gebeurtenissen in deze selectie."
              : "Geen gebeurtenissen in de laatste 60 dagen."
          }
          onReset={gekozen.length ? () => setGekozen([]) : undefined}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((it) => (
            <FeedRow key={it.id} item={it} onOpen={setOpen} />
          ))}
        </div>
      )}

      <SlidePanel open={open !== null} onClose={sluit} terugLabel="Terug naar de feed">
        {open && open.itemKind === "trend" && trendlaagAan() && <TrendVanBevinding id={open.subject.id} onClose={sluit} />}
        {open && trend && <TrendDetail trend={trend} onClose={sluit} />}
        {open && skill && (
          <SkillDetail
            skill={skill.skill as Skill}
            trends={skill.contributingTrends ?? []}
            onClose={sluit}
          />
        )}
        {open && bron && (
          <SourceDetail source={bron} otherFromPublisher={[]} onClose={sluit} />
        )}
        {open && !trend && !skill && !bron && (
          <div style={{ padding: 24 }}>
            <p style={{ fontSize: size.body, color: color.faint }}>
              {detailFout ?? "Ophalen…"}
            </p>
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
